import { layersApi } from '@/api/layers';
import {
  ScenarioLayer,
  ScenarioLayerAnalogueHit,
  scenarioLayersApi,
} from '@/api/scenarioLayers';
import { isHiddenLayerAttribute } from '@/data/hiddenLayerAttributes';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

const SLIDER_SPAN_FRACTION = 0.95;
const PROPERTY_DECIMAL_PLACES = 3;
const NAME_COLUMN_WIDTH = 220;
const VALUE_FIELD_WIDTH = 96;

interface CatalogAttribute {
  id: string;
  name: string;
  unit?: string | null;
  type: string;
}

interface PropertyFilterRow {
  attributeId: string;
  attributeName: string;
  unit?: string | null;
  actualValue: number | null;
  included: boolean;
  min: number | null;
  max: number | null;
}

interface AnaloguePropertySearchDialogProps {
  open: boolean;
  layer: ScenarioLayer | null;
  excludeLayerIds: string[];
  adding?: boolean;
  onClose: () => void;
  onAdd: (hit: ScenarioLayerAnalogueHit) => void;
}

function getLayerNumeric(layer: ScenarioLayer, attributeId: string): number | null {
  const prop = layer.properties?.find((item) => item.attribute_id === attributeId);
  if (!prop) return null;
  if (prop.float_value != null && Number.isFinite(prop.float_value)) return prop.float_value;
  if (prop.integer_value != null && Number.isFinite(prop.integer_value)) return prop.integer_value;
  return null;
}

function formatBoundValue(value: number): string {
  return value.toFixed(PROPERTY_DECIMAL_PLACES);
}

function parseBoundInput(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.');
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function getDefaultRangeFromDeviation(actualValue: number, deviationPct: number): { min: number; max: number } {
  const deviationFrac = Math.max(0, Math.min(100, deviationPct)) / 100;
  if (actualValue === 0) {
    const span = Math.max(0.001, deviationFrac * 0.01);
    return { min: -span, max: span };
  }
  if (actualValue > 0) {
    return {
      min: actualValue * (1 - deviationFrac),
      max: actualValue * (1 + deviationFrac),
    };
  }
  return {
    min: actualValue * (1 + deviationFrac),
    max: actualValue * (1 - deviationFrac),
  };
}

function getSliderTrackBounds(
  actualValue: number | null,
  min: number | null,
  max: number | null,
): { trackMin: number; trackMax: number } {
  const points: number[] = [];
  if (actualValue != null && Number.isFinite(actualValue)) {
    if (actualValue === 0) {
      points.push(-0.1, 0.1);
    } else if (actualValue > 0) {
      points.push(actualValue * (1 - SLIDER_SPAN_FRACTION), actualValue * (1 + SLIDER_SPAN_FRACTION));
    } else {
      points.push(actualValue * (1 + SLIDER_SPAN_FRACTION), actualValue * (1 - SLIDER_SPAN_FRACTION));
    }
    points.push(actualValue);
  }
  if (min != null && Number.isFinite(min)) points.push(min);
  if (max != null && Number.isFinite(max)) points.push(max);
  if (points.length < 2) {
    return { trackMin: 0, trackMax: 1 };
  }
  let trackMin = Math.min(...points);
  let trackMax = Math.max(...points);
  if (trackMin === trackMax) {
    const pad = Math.abs(trackMin) * 0.05 || 0.1;
    trackMin -= pad;
    trackMax += pad;
  }
  return { trackMin, trackMax };
}

function getSliderStep(trackMin: number, trackMax: number): number {
  const span = Math.abs(trackMax - trackMin);
  if (!Number.isFinite(span) || span <= 0) return 0.001;
  const rough = span / 200;
  if (rough >= 1) return Number(rough.toPrecision(2));
  if (rough >= 0.01) return Number(rough.toFixed(3));
  return Number(rough.toPrecision(2));
}

function buildRows(
  attributes: CatalogAttribute[],
  layer: ScenarioLayer,
  maxima: Record<string, number>,
): PropertyFilterRow[] {
  return attributes
    .filter((attr) => attr.type === 'float' || attr.type === 'integer')
    .filter((attr) => !isHiddenLayerAttribute(attr.id, attr.name))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((attr) => {
      const actualValue = getLayerNumeric(layer, attr.id);
      const dbMax = maxima[attr.id];
      const unsetMax = dbMax != null && dbMax > 0 ? dbMax * 2 : 1;
      return {
        attributeId: attr.id,
        attributeName: attr.name,
        unit: attr.unit,
        actualValue,
        included: actualValue != null,
        min: actualValue == null ? 0 : null,
        max: actualValue == null ? unsetMax : null,
      };
    });
}

function getUnsetRangeMax(attributeId: string, maxima: Record<string, number>): number {
  const dbMax = maxima[attributeId];
  return dbMax != null && dbMax > 0 ? dbMax * 2 : 1;
}

function applyDeviationPct(
  rows: PropertyFilterRow[],
  deviationPct: number,
  maxima: Record<string, number>,
): PropertyFilterRow[] {
  return rows.map((row) => {
    if (row.actualValue == null) {
      const unsetMax = getUnsetRangeMax(row.attributeId, maxima);
      return { ...row, included: true, min: 0, max: unsetMax };
    }
    const bounds = getDefaultRangeFromDeviation(row.actualValue, deviationPct);
    return { ...row, included: true, min: bounds.min, max: bounds.max };
  });
}

interface RangeRowProps {
  row: PropertyFilterRow;
  maxima: Record<string, number>;
  onChange: (next: PropertyFilterRow) => void;
}

const RangeRow: React.FC<RangeRowProps> = ({ row, maxima, onChange }) => {
  const unsetMax = getUnsetRangeMax(row.attributeId, maxima);
  const { trackMin, trackMax } = useMemo(() => {
    if (row.actualValue == null) {
      return { trackMin: 0, trackMax: unsetMax };
    }
    return getSliderTrackBounds(row.actualValue, row.min, row.max);
  }, [row.actualValue, row.min, row.max, unsetMax]);
  const step = useMemo(() => getSliderStep(trackMin, trackMax), [trackMin, trackMax]);
  const [minInput, setMinInput] = useState(row.min == null ? '' : formatBoundValue(row.min));
  const [maxInput, setMaxInput] = useState(row.max == null ? '' : formatBoundValue(row.max));

  useEffect(() => {
    setMinInput(row.min == null ? '' : formatBoundValue(row.min));
    setMaxInput(row.max == null ? '' : formatBoundValue(row.max));
  }, [row.min, row.max]);

  const sliderEnabled = row.included && (row.actualValue != null || (row.min != null && row.max != null));
  const sliderValue: [number, number] = useMemo(() => {
    if (row.min != null && row.max != null) {
      return [Math.min(row.min, row.max), Math.max(row.min, row.max)];
    }
    if (row.actualValue != null) {
      return [row.actualValue, row.actualValue];
    }
    return [trackMin, trackMax];
  }, [row.min, row.max, row.actualValue, trackMin, trackMax]);

  const commitMin = (raw: string) => {
    if (!raw.trim()) {
      onChange({ ...row, min: null });
      return;
    }
    const parsed = parseBoundInput(raw);
    if (parsed == null) {
      setMinInput(row.min == null ? '' : formatBoundValue(row.min));
      return;
    }
    onChange({ ...row, min: parsed });
  };

  const commitMax = (raw: string) => {
    if (!raw.trim()) {
      onChange({ ...row, max: null });
      return;
    }
    const parsed = parseBoundInput(raw);
    if (parsed == null) {
      setMaxInput(row.max == null ? '' : formatBoundValue(row.max));
      return;
    }
    onChange({ ...row, max: parsed });
  };

  const nameLabel = row.unit ? `${row.attributeName} (${row.unit})` : row.attributeName;
  const marks = row.actualValue == null
    ? undefined
    : [
        {
          value: row.actualValue,
          label: (
            <Typography
              variant="caption"
              sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'warning.dark', whiteSpace: 'nowrap' }}
            >
              {formatBoundValue(row.actualValue)}
            </Typography>
          ),
        },
      ];

  return (
    <Box sx={{ mb: 2.5, opacity: row.included ? 1 : 0.55 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', sm: NAME_COLUMN_WIDTH },
            minWidth: { sm: NAME_COLUMN_WIDTH },
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {nameLabel}
          </Typography>
          <FormControlLabel
            sx={{ mt: 0.25, ml: -0.5, mr: 0 }}
            control={
              <Checkbox
                size="small"
                checked={row.included}
                onChange={(_, checked) => {
                  if (checked && row.actualValue == null && (row.min == null || row.max == null)) {
                    onChange({ ...row, included: true, min: 0, max: unsetMax });
                    return;
                  }
                  onChange({ ...row, included: checked });
                }}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                Use in search
              </Typography>
            }
          />
        </Box>

        <TextField
          label="Min"
          size="small"
          value={minInput}
          onChange={(e) => setMinInput(e.target.value)}
          onBlur={() => commitMin(minInput)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitMin(minInput);
          }}
          sx={{ width: VALUE_FIELD_WIDTH, flexShrink: 0 }}
          inputProps={{ inputMode: 'decimal' }}
        />

        <TextField
          label="Max"
          size="small"
          value={maxInput}
          onChange={(e) => setMaxInput(e.target.value)}
          onBlur={() => commitMax(maxInput)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitMax(maxInput);
          }}
          sx={{ width: VALUE_FIELD_WIDTH, flexShrink: 0 }}
          inputProps={{ inputMode: 'decimal' }}
        />

        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 160 }, px: { xs: 0, sm: 0.5 } }}>
          <Slider
            disabled={!sliderEnabled}
            value={sliderValue}
            min={trackMin}
            max={trackMax}
            step={step}
            marks={marks}
            valueLabelDisplay="auto"
            valueLabelFormat={formatBoundValue}
            onChange={(_, newValue) => {
              const [rawMin, rawMax] = newValue as number[];
              onChange({
                ...row,
                included: true,
                min: Math.min(rawMin, rawMax),
                max: Math.max(rawMin, rawMax),
              });
            }}
            sx={{
              '& .MuiSlider-mark': {
                height: 14,
                width: 3,
                borderRadius: 1,
                backgroundColor: 'warning.main',
              },
              '& .MuiSlider-markLabel': {
                top: 28,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export const AnaloguePropertySearchDialog: React.FC<AnaloguePropertySearchDialogProps> = ({
  open,
  layer,
  excludeLayerIds,
  adding = false,
  onClose,
  onAdd,
}) => {
  const [attributes, setAttributes] = useState<CatalogAttribute[]>([]);
  const [propertyMaxima, setPropertyMaxima] = useState<Record<string, number>>({});
  const [rows, setRows] = useState<PropertyFilterRow[]>([]);
  const [loadingAttrs, setLoadingAttrs] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hits, setHits] = useState<ScenarioLayerAnalogueHit[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [setAllPct, setSetAllPct] = useState('10');

  useEffect(() => {
    if (!open) return;
    setError(null);
    setHits([]);
    setHasSearched(false);
    setSetAllPct('10');
    let cancelled = false;
    setLoadingAttrs(true);
    Promise.all([layersApi.getLayerAttributes(), scenarioLayersApi.getAnaloguePropertyMaxima()])
      .then(([data, maxima]) => {
        if (cancelled) return;
        setAttributes(data);
        setPropertyMaxima(maxima || {});
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load reservoir attributes');
      })
      .finally(() => {
        if (!cancelled) setLoadingAttrs(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, layer?.scenario_layer_id]);

  useEffect(() => {
    if (!open || !layer) {
      setRows([]);
      return;
    }
    setRows(buildRows(attributes, layer, propertyMaxima));
  }, [open, layer, attributes, propertyMaxima]);

  const setAllPctNum = parseBoundInput(setAllPct);
  const setAllPctInvalid = setAllPctNum == null || setAllPctNum < 0 || setAllPctNum > 100;

  const handleSetToAll = () => {
    if (setAllPctNum == null || setAllPctInvalid) return;
    setRows((prev) => applyDeviationPct(prev, setAllPctNum, propertyMaxima));
  };

  const includeAllAttributes = () => {
    setRows((prev) => prev.map((row) => {
      if (row.actualValue != null) {
        return { ...row, included: true };
      }
      const unsetMax = getUnsetRangeMax(row.attributeId, propertyMaxima);
      return { ...row, included: true, min: 0, max: unsetMax };
    }));
  };

  const excludeAllAttributes = () => {
    setRows((prev) => prev.map((row) => ({ ...row, included: false })));
  };

  const activeFilters = rows
    .filter((row) => row.included && (row.min != null || row.max != null))
    .map((row) => ({
      attribute_id: row.attributeId,
      min_value: row.min,
      max_value: row.max,
    }));

  const runSearch = async () => {
    if (!layer) return;
    if (!activeFilters.length) {
      setError('Include at least one property range');
      return;
    }
    setSearching(true);
    setError(null);
    setHasSearched(true);
    try {
      setHits(await scenarioLayersApi.searchAnaloguesByProperties(
        layer.scenario_layer_id,
        activeFilters,
        excludeLayerIds,
      ));
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Property search failed');
      setHits([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Add analogue by properties</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Included ranges are combined with AND. A candidate without a value for an included
          property is not a match. Only company or public layers with oil and water-cut type-well
          coefficients are searched. If more than 10 match, company layers are listed first.
          The orange mark is the current reservoir value; the range does not have to include it.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            label="Deviation (%)"
            size="small"
            type="number"
            value={setAllPct}
            onChange={(e) => setSetAllPct(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSetToAll();
            }}
            inputProps={{ min: 0, max: 100, step: 0.1 }}
            error={setAllPct.length > 0 && setAllPctInvalid}
            helperText={setAllPct.length > 0 && setAllPctInvalid ? 'Enter 0–100' : undefined}
            sx={{ width: 140 }}
          />
          <Button variant="outlined" onClick={handleSetToAll} disabled={setAllPctInvalid || !rows.length}>
            Set to all
          </Button>
          <Button variant="outlined" onClick={includeAllAttributes} disabled={!rows.length}>
            Include all
          </Button>
          <Button variant="outlined" onClick={excludeAllAttributes} disabled={!rows.length}>
            Exclude all
          </Button>
          <Typography variant="caption" color="text.secondary">
            Set to all: ±% around filled properties; unset properties get 0 to 2× DB maximum.
            Include/exclude all toggles every attribute for the search.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loadingAttrs ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ maxHeight: 420, overflowY: 'auto', pr: 1 }}>
            {rows.map((row) => (
              <RangeRow
                key={row.attributeId}
                row={row}
                maxima={propertyMaxima}
                onChange={(next) => {
                  setRows((prev) => prev.map((item) => (
                    item.attributeId === next.attributeId ? next : item
                  )));
                }}
              />
            ))}
            {!rows.length && (
              <Alert severity="info">No numeric reservoir attributes found.</Alert>
            )}
          </Box>
        )}

        <Box sx={{ mt: 2, mb: 2 }}>
          <Button variant="contained" onClick={runSearch} disabled={searching || loadingAttrs || !layer}>
            {searching ? 'Searching…' : 'Search'}
          </Button>
        </Box>

        {searching ? (
          <CircularProgress size={24} />
        ) : hasSearched && hits.length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Field</TableCell>
                <TableCell>Reservoir</TableCell>
                <TableCell>Recovery factor</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {hits.map((hit) => {
                const alreadyAdded = excludeLayerIds.includes(hit.layer_id);
                const slotsFull = excludeLayerIds.length >= 10;
                return (
                <TableRow key={hit.layer_id}>
                  <TableCell>{hit.field_name}</TableCell>
                  <TableCell>{hit.layer_name}</TableCell>
                  <TableCell>{hit.recovery_factor == null ? '—' : hit.recovery_factor.toFixed(3)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => onAdd(hit)}
                      disabled={alreadyAdded || adding || (!alreadyAdded && slotsFull)}
                    >
                      {alreadyAdded ? 'Added' : 'Add'}
                    </Button>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : hasSearched ? (
          <Typography variant="body2" color="text.secondary">
            No matching layers with oil and water-cut type-well curves.
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
